import { useState, useCallback, useRef } from 'react';
import { IActionContext } from '@msdyn365-commerce/core-internal';
import { CategoryProductsResponse } from '../../../integrations/relewise/types/mapped';
import { DEFAULT_PAGE_SIZE, NUMBER_OF_VARIANTS_PER_PRODUCT_IN_PLP } from '../../../integrations/relewise/const';
import { FilterOptions, parseFilterOptions, writeFilterOptionsToParams } from '../../../utils/productListFilters';
import { ProductListSortOption } from '../../../utils/productListSort';
import { CusProductListFiltersConfig, CusProductListUseFiltersResult } from '../cus-product-list.data';
import { useCategoryService, useSearchService } from '../../../integrations/relewise/hooks/serviceHooks';
import { PRODUCT_LIST_MODULE_NAME } from '../cus-product-list.const';

export default function useFilters(actionContext: IActionContext, config: CusProductListFiltersConfig): CusProductListUseFiltersResult {
    const [filteredResults, setFilteredResults] = useState<CategoryProductsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const initialSort: ProductListSortOption = config.initialSort ?? 'recommended';
    const [currentSort, setCurrentSortState] = useState<ProductListSortOption>(initialSort);
    const sortRef = useRef<ProductListSortOption>(initialSort);
    const categoryRecordId = config.mode === 'category' ? config.categoryRecordId : '';
    const searchQuery = config.mode === 'search' ? config.query : '';

    const searchService = useSearchService(actionContext.requestContext, actionContext.telemetry);
    const categoryService = useCategoryService(actionContext.requestContext, actionContext.telemetry);

    const fetchProducts = useCallback(
        async (filterOptions: FilterOptions | undefined, page: number, sort: ProductListSortOption) => {
            if (typeof window === 'undefined') {
                return;
            }

            const itemsPerPage = config.itemsPerPage ?? DEFAULT_PAGE_SIZE;
            const variantsPerProduct = config.variantsPerProduct ?? NUMBER_OF_VARIANTS_PER_PRODUCT_IN_PLP;

            setIsLoading(true);
            try {
                let result: CategoryProductsResponse | undefined;

                if (config.mode === 'search') {
                    result = await searchService.getSearchProducts(
                        searchQuery,
                        itemsPerPage,
                        page,
                        filterOptions,
                        sort,
                        variantsPerProduct
                    );
                } else {
                    result = await categoryService.getCategoryProducts(
                        categoryRecordId,
                        itemsPerPage,
                        page,
                        sort,
                        variantsPerProduct,
                        filterOptions
                    );
                }

                if (result) {
                    setFilteredResults(result);
                }
            } catch (error) {
                const message = `[${PRODUCT_LIST_MODULE_NAME}] Error fetching products in client side: ${error instanceof Error ? error.message : String(error)}`;
                actionContext.telemetry.error(message);
                console.error(message);
            } finally {
                setIsLoading(false);
            }
        },
        [
            actionContext,
            searchService,
            categoryService,
            config.mode,
            config.itemsPerPage,
            config.variantsPerProduct,
            searchQuery,
            categoryRecordId
        ]
    );

    const search = useCallback(
        async (filterOptions: FilterOptions) => {
            if (typeof window === 'undefined') {
                return;
            }

            const hasFilters = Object.keys(filterOptions).length > 0;
            const params = new URLSearchParams(window.location.search);

            writeFilterOptionsToParams(params, filterOptions);
            // Reset to page 1 when filters change
            params.delete('page');
            const qs = params.toString();
            window.history.replaceState(null, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);

            await fetchProducts(hasFilters ? filterOptions : undefined, 1, sortRef.current);
        },
        [fetchProducts]
    );

    const goToPage = useCallback(
        async (page: number) => {
            if (typeof window === 'undefined') {
                return;
            }

            const params = new URLSearchParams(window.location.search);

            // Read current filters before updating URL
            const filterOptions = parseFilterOptions(k => params.get(k));

            params.set('page', page.toString());
            window.history.pushState(null, '', `${window.location.pathname}?${params.toString()}`);

            await fetchProducts(filterOptions, page, sortRef.current);
        },
        [fetchProducts]
    );

    const setSort = useCallback(
        async (sort: ProductListSortOption) => {
            if (typeof window === 'undefined') {
                return;
            }

            // Read current filters from URL
            const params = new URLSearchParams(window.location.search);
            const filterOptions = parseFilterOptions(k => params.get(k));

            // Reset pagination to page 1 on sort change
            params.delete('page');

            // Persist sort in URL so it survives sharing/refresh; omit default 'recommended'.
            if (sort === 'recommended') {
                params.delete('sort');
            } else {
                params.set('sort', sort);
            }

            const qs = params.toString();
            window.history.replaceState(null, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);

            sortRef.current = sort;
            setCurrentSortState(sort);
            await fetchProducts(filterOptions, 1, sort);
        },
        [fetchProducts]
    );

    return { filteredResults, isLoading, search, goToPage, setSort, currentSort };
}
